import React, { useState, useEffect, useMemo } from "react";
import { Wallet, Plus, Trash2, TrendingUp, TrendingDown, PiggyBank, Printer } from "lucide-react";
import { useMember } from "../context/MemberContext";
import { fetchToolData, saveToolData } from "../lib/toolCloud";

const STORAGE_KEY = "dugun_butcem_v1";

const DEFAULT_ITEMS = [
  "Düğün Mekanı",
  "Yemek & İkram",
  "Gelinlik & Damatlık",
  "Fotoğraf & Video",
  "Müzik & Eğlence",
  "Çiçek & Süsleme",
  "Davetiye",
  "Nikah Şekeri & Hediyelik",
  "Alyans",
  "Kuaför & Makyaj",
  "Ulaşım",
  "Balayı",
];

const uid = () => Math.random().toString(36).slice(2, 9);
const fmtTL = (n) => (Number(n) || 0).toLocaleString("tr-TR") + " ₺";
const freshItems = () => DEFAULT_ITEMS.map((c) => ({ id: uid(), category: c, planned: 0, spent: 0 }));

const BudgetTool = () => {
  const { token, member } = useMember();
  const mid = member?.id;
  const [totalBudget, setTotalBudget] = useState(0);
  const [items, setItems] = useState(freshItems);
  const [newCat, setNewCat] = useState("");
  const [cloudReady, setCloudReady] = useState(false);

  useEffect(() => {
    let active = true;
    setCloudReady(false);
    (async () => {
      let d = await fetchToolData("butcem", token);
      if (!d && mid) {
        try { const raw = localStorage.getItem(`${STORAGE_KEY}_${mid}`); if (raw) d = JSON.parse(raw); } catch {}
      }
      if (active) {
        setTotalBudget(d?.totalBudget ?? 0);
        setItems(Array.isArray(d?.items) && d.items.length ? d.items : freshItems());
        setCloudReady(true);
      }
    })();
    return () => { active = false; };
  }, [token, mid]);

  useEffect(() => {
    if (!cloudReady || !mid) return;
    localStorage.setItem(`${STORAGE_KEY}_${mid}`, JSON.stringify({ totalBudget, items }));
    if (token) saveToolData("butcem", token, { totalBudget, items });
  }, [totalBudget, items, cloudReady, token, mid]);

  const totalPlanned = useMemo(() => items.reduce((s, i) => s + (Number(i.planned) || 0), 0), [items]);
  const totalSpent = useMemo(() => items.reduce((s, i) => s + (Number(i.spent) || 0), 0), [items]);
  const budget = Number(totalBudget) || 0;
  const remaining = budget - totalSpent;

  const update = (id, field, value) =>
    setItems((its) => its.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
  const remove = (id) => setItems((its) => its.filter((i) => i.id !== id));

  const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

  const exportPdf = () => {
    const today = new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
    const rows = items
      .map((i) => {
        const diff = (Number(i.planned) || 0) - (Number(i.spent) || 0);
        return `<tr><td>${esc(i.category)}</td><td class="num">${fmtTL(i.planned)}</td><td class="num">${fmtTL(i.spent)}</td><td class="num ${diff < 0 ? "neg" : "pos"}">${fmtTL(diff)}</td></tr>`;
      })
      .join("");
    const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Düğün Bütçe Özeti</title>
<style>
  *{box-sizing:border-box;font-family:'Segoe UI',Arial,sans-serif}
  body{margin:0;padding:40px;color:#2A2224}
  .head{border-bottom:3px solid #9E2A3B;padding-bottom:16px;margin-bottom:24px}
  h1{color:#9E2A3B;margin:0 0 4px;font-size:24px}
  .sub{color:#7C6E70;font-size:13px}
  .cards{display:flex;gap:12px;margin-bottom:24px;flex-wrap:wrap}
  .card{flex:1;min-width:130px;border:1px solid #F0DDE0;border-radius:12px;padding:14px;background:#FBF5F0}
  .card .l{font-size:12px;color:#7C6E70}
  .card .v{font-size:20px;font-weight:700;margin-top:4px}
  .card.main{background:#9E2A3B;color:#fff;border:none}
  .card.main .l{color:rgba(255,255,255,.8)}
  table{width:100%;border-collapse:collapse;font-size:14px}
  th,td{text-align:left;padding:10px 12px;border-bottom:1px solid #F0DDE0}
  th{color:#7C6E70;font-weight:600;font-size:12px;text-transform:uppercase}
  td.num,th.num{text-align:right}
  tfoot td{font-weight:700;border-top:2px solid #9E2A3B;border-bottom:none}
  .neg{color:#C0392B}.pos{color:#2f7d4f}
  .foot{margin-top:30px;color:#9c8f90;font-size:11px;text-align:center}
</style></head><body>
  <div class="head"><h1>Düğün Bütçe Özeti</h1><div class="sub">DüğünSenin.com · ${today}</div></div>
  <div class="cards">
    <div class="card main"><div class="l">Toplam Bütçe</div><div class="v">${fmtTL(budget)}</div></div>
    <div class="card"><div class="l">Planlanan</div><div class="v">${fmtTL(totalPlanned)}</div></div>
    <div class="card"><div class="l">Harcanan</div><div class="v">${fmtTL(totalSpent)}</div></div>
    <div class="card"><div class="l">Kalan</div><div class="v ${remaining < 0 ? "neg" : "pos"}">${fmtTL(remaining)}</div></div>
  </div>
  <table>
    <thead><tr><th>Kategori</th><th class="num">Planlanan</th><th class="num">Harcanan</th><th class="num">Fark</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td>Toplam</td><td class="num">${fmtTL(totalPlanned)}</td><td class="num">${fmtTL(totalSpent)}</td><td class="num ${totalPlanned - totalSpent < 0 ? "neg" : "pos"}">${fmtTL(totalPlanned - totalSpent)}</td></tr></tfoot>
  </table>
  <div class="foot">Bu özet DüğünSenin.com Bütçem aracı ile oluşturulmuştur.</div>
  <script>window.onload=function(){window.print()}<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  const add = (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    setItems((its) => [...its, { id: uid(), category: newCat.trim(), planned: 0, spent: 0 }]);
    setNewCat("");
  };

  return (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 sm:p-8" data-testid="budget-tool">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center">
            <Wallet className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-[#2A2224]">Düğün Bütçem</h1>
            <p className="text-sm text-[#7C6E70]">Kategori bazlı harcama planlama ve takip</p>
          </div>
        </div>
        <button
          onClick={exportPdf}
          data-testid="budget-pdf-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#9E2A3B] text-[#9E2A3B] text-sm font-medium hover:bg-[#9E2A3B] hover:text-white transition-colors shrink-0"
        >
          <Printer className="w-4 h-4" /> <span className="hidden sm:inline">PDF / Yazdır</span>
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] text-white p-4">
          <p className="text-xs text-white/80">Toplam Bütçe</p>
          <input
            data-testid="budget-total-input"
            type="number"
            min="0"
            value={totalBudget}
            onChange={(e) => setTotalBudget(e.target.value)}
            className="w-full bg-transparent text-2xl font-bold outline-none placeholder-white/60"
            placeholder="0"
          />
          <p className="text-xs text-white/70">₺ olarak girin</p>
        </div>
        <SummaryCard icon={PiggyBank} label="Planlanan" value={fmtTL(totalPlanned)} testid="budget-planned" />
        <SummaryCard icon={TrendingDown} label="Harcanan" value={fmtTL(totalSpent)} testid="budget-spent" />
        <SummaryCard
          icon={TrendingUp}
          label="Kalan"
          value={fmtTL(remaining)}
          testid="budget-remaining"
          danger={remaining < 0}
        />
      </div>

      {budget > 0 && (
        <div className="mt-4">
          <div className="h-2.5 rounded-full bg-[#F6E4E6] overflow-hidden">
            <div
              className={`h-full transition-all ${totalSpent > budget ? "bg-[#C0392B]" : "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B]"}`}
              style={{ width: `${Math.min(100, budget ? (totalSpent / budget) * 100 : 0)}%` }}
            />
          </div>
          <p className="text-xs text-[#9c8f90] mt-1.5">
            Bütçenin %{budget ? Math.round((totalSpent / budget) * 100) : 0}'i harcandı
            {remaining < 0 && <span className="text-[#C0392B] font-medium"> · Bütçe aşıldı!</span>}
          </p>
        </div>
      )}

      {/* Table */}
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]" data-testid="budget-table">
          <thead>
            <tr className="text-left text-[#7C6E70] border-b border-[#F0DDE0]">
              <th className="py-2.5 pr-3 font-medium">Kategori</th>
              <th className="py-2.5 px-3 font-medium w-36">Planlanan (₺)</th>
              <th className="py-2.5 px-3 font-medium w-36">Harcanan (₺)</th>
              <th className="py-2.5 px-3 font-medium w-28 text-right">Fark</th>
              <th className="py-2.5 pl-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const diff = (Number(i.planned) || 0) - (Number(i.spent) || 0);
              return (
                <tr key={i.id} className="border-b border-[#F6E4E6] group">
                  <td className="py-2 pr-3">
                    <input
                      value={i.category}
                      onChange={(e) => update(i.id, "category", e.target.value)}
                      className="w-full bg-transparent font-medium text-[#2A2224] outline-none focus:text-[#9E2A3B]"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <input
                      data-testid={`budget-planned-${i.id}`}
                      type="number"
                      min="0"
                      value={i.planned}
                      onChange={(e) => update(i.id, "planned", e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-[#F0DDE0] outline-none focus:border-[#9E2A3B]"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <input
                      data-testid={`budget-spent-${i.id}`}
                      type="number"
                      min="0"
                      value={i.spent}
                      onChange={(e) => update(i.id, "spent", e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-[#F0DDE0] outline-none focus:border-[#9E2A3B]"
                    />
                  </td>
                  <td className={`py-2 px-3 text-right font-medium ${diff < 0 ? "text-[#C0392B]" : "text-[#2f7d4f]"}`}>
                    {fmtTL(diff)}
                  </td>
                  <td className="py-2 pl-3 text-right">
                    <button onClick={() => remove(i.id)} data-testid={`budget-remove-${i.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#c9a0a6] hover:text-[#9E2A3B]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-semibold text-[#2A2224] border-t-2 border-[#F0DDE0]">
              <td className="py-3 pr-3">Toplam</td>
              <td className="py-3 px-3">{fmtTL(totalPlanned)}</td>
              <td className="py-3 px-3">{fmtTL(totalSpent)}</td>
              <td className={`py-3 px-3 text-right ${totalPlanned - totalSpent < 0 ? "text-[#C0392B]" : "text-[#2f7d4f]"}`}>
                {fmtTL(totalPlanned - totalSpent)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Add category */}
      <form onSubmit={add} className="mt-5 flex gap-3 max-w-md">
        <input
          data-testid="budget-new-cat"
          value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          placeholder="Yeni kategori ekle..."
          className="flex-1 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
        />
        <button data-testid="budget-add-btn" type="submit" className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#861f2f] transition-colors">
          <Plus className="w-4 h-4" /> Ekle
        </button>
      </form>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, testid, danger }) => (
  <div className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] p-4">
    <Icon className={`w-5 h-5 mb-2 ${danger ? "text-[#C0392B]" : "text-[#C15B6B]"}`} />
    <p className="text-xs text-[#7C6E70]">{label}</p>
    <p data-testid={testid} className={`text-xl font-bold ${danger ? "text-[#C0392B]" : "text-[#2A2224]"}`}>{value}</p>
  </div>
);

export default BudgetTool;
